import React, { ReactElement } from "react";
import {
    finalShowForm,
    orderListByKeyword,
    replacePlaceholders,
} from "@lib/sharedUtils";
import { useRouter } from "next/router";
import dynamic from "next/dynamic";
import { useDomainContext } from "@hooks/useDomainContext";
import { TAffiliateOfferList } from "@page-builder/ts/generated";
import { ClickOffer, ListingItem } from "@lib/shared/types";
import MortgageOffer from "@components/shared/mortgageOffer";
import styles from "./style.module.scss";
import { ThumbtackOfferType } from "./thumbtackOffer";
import ThumbtackOffer from "./thumbtackOffer";
const Offer = dynamic(() => import("./Offer"));

export default function AffiliateOffersList({
    props = {},
    inForm = false,
}: {
    props?: TAffiliateOfferList["props"];
    inForm?: boolean;
}): ReactElement {
    const router = useRouter();
    const {
        domain,
        clicksOffers,
        pageBuilderListings,
        visitDetails,
        category,
        angiWidgetDetails,
    } = useDomainContext();
    let offers: ClickOffer[] | ListingItem[] | ThumbtackOfferType[] = [];
    const keyword = router.query?.k as string;

    const filterAutoOffersList = (list: ListingItem[]): ListingItem[] => {
        const autoOffersList = props?.autoOffersList || [];
        const shouldFilterByAutoOffersList = autoOffersList.length > 0;
        const allowedIds = autoOffersList.map((offer) => offer.id);
        return list.filter(
            (offer) =>
                offer?.features.length &&
                offer?.placements?.skipMainOffersList !== true &&
                (shouldFilterByAutoOffersList
                    ? allowedIds.includes(offer.id)
                    : true),
        );
    };

    const showOffersInsteadOfForm = !finalShowForm(
        router.query.sl as string,
        domain.showForm,
        domain.defaultOffers,
    );

    switch (props.source) {
        case "auto":
            offers = orderListByKeyword(
                keyword,
                filterAutoOffersList(domain?.defaultOffers),
            );
            break;
        case "external":
            if (clicksOffers) {
                offers = clicksOffers;
            } else {
                offers = [];
            }
            break;
        default: {
            if (!props.source) {
                offers = orderListByKeyword(
                    keyword,
                    filterAutoOffersList(domain?.defaultOffers),
                );
                break;
            }
            if (pageBuilderListings && props.source) {
                offers = pageBuilderListings[props.source]?.offers ?? [];
            }
            break;
        }
    }

    // Early return if zone is thankYou and widget should not be shown
    // angiWidgetDetails.showWidget === undefined case is needed to avoid hiding offers on initial render before angiWidgetDetails is set)
    if (
        props.zone === "thankYou" &&
        props?.thumbtackFormat === true &&
        (angiWidgetDetails.showWidget === undefined ||
            angiWidgetDetails.showWidget === true)
    ) {
        return <></>;
    }

    if (Array.isArray(offers) && offers.length) {
        const getOffersContent = () => {
            const slicedOffers = offers.slice(
                showOffersInsteadOfForm && !inForm ? 1 : 0,
                props.maxNumberOfOffers,
            );
            if (props?.thumbtackFormat) {
                return (
                    <div className={styles.thumbtackOfferList}>
                        {slicedOffers.map((offer, index) => (
                            <ThumbtackOffer
                                key={index}
                                offer={offer as ThumbtackOfferType}
                            />
                        ))}
                    </div>
                );
            }
            return (
                <div className={styles.container}>
                    {slicedOffers?.map((offer, index) => {
                        const isExternal = !("displayUrl" in offer);
                        const isMortgage =
                            isExternal && (offer as ClickOffer)?.isMortgage;

                        return !isMortgage ? (
                            <Offer
                                offer={!isExternal ? offer : undefined}
                                key={index}
                                index={index}
                                logoUrl={
                                    isExternal
                                        ? (offer as ClickOffer).logoUrl
                                        : offer.logo?.fullPath
                                }
                                isExternal={isExternal}
                                buttonText={
                                    isExternal
                                        ? props.buttonText ?? "View Deal"
                                        : offer?.hiddenAttributes.buttonText ??
                                          (props.buttonText || "Get Quote")
                                }
                                title={
                                    isExternal
                                        ? (offer as ClickOffer).title
                                        : offer.description
                                }
                                bulletedDescription={
                                    isExternal
                                        ? (offer as ClickOffer)
                                              .bulletedDescription
                                        : offer.features
                                }
                                revenue={
                                    isExternal
                                        ? (offer as ClickOffer).revenue
                                        : undefined
                                }
                                slug={offer.slug}
                                featuredOfferMessage={
                                    props.featuredOfferMessage
                                }
                                featuredOfferMessagePosition={
                                    props.featuredOfferMessagePosition
                                }
                                ratingPosition={props.ratingPosition}
                                showIndex={props.showIndex}
                                showRating={props.showRating}
                                showSummary={props.showSummary}
                                showSeparator={props.showSeparator}
                                zone={props.zone}
                                useHiddenAttributes={props.useHiddenAttributes}
                                textUnderButton={props.textUnderButton}
                                hiddenAttributes={
                                    isExternal
                                        ? {}
                                        : offer.hiddenAttributes ?? {}
                                }
                                hideRatingText={props.hideRatingText}
                                starRating={props.starRating}
                                onlyButtonAndLinksClickable={
                                    props.onlyButtonAndLinksClickable
                                }
                                maxOffersWithAction={props.maxOffersWithAction}
                                actionButtonText={props.actionButtonText}
                                hideDescription={props.hideDescription}
                            />
                        ) : (
                            <MortgageOffer
                                key={index}
                                offer={offer as ClickOffer}
                            />
                        );
                    })}
                </div>
            );
        };
        return (
            <section>
                {props.title && (
                    <div
                        className={`mb-6 ${styles.sectionTitle}`}
                        dangerouslySetInnerHTML={{
                            __html: replacePlaceholders(props.title, {
                                category: category?.name,
                                region: visitDetails?.region,
                                city: visitDetails?.city,
                            }),
                        }}
                    />
                )}
                {props.description && (
                    <div
                        className={styles.description}
                        dangerouslySetInnerHTML={{
                            __html: replacePlaceholders(props.description, {
                                category: category?.name,
                                region: visitDetails?.region,
                                city: visitDetails?.city,
                            }),
                        }}
                    />
                )}
                {getOffersContent()}
            </section>
        );
    }
    return <></>;
}
